import { SensorType } from '@frontend/device/types';
import { Feature } from '@frontend/functionality/types';
import { Logger } from '@frontend/logger';
import { useAccountRepository } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { type TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import type { IUser } from '@frontend/user/types';
import type { MobileWizardStateRecord } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useScanners } from '../../../hooks';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { findUsersByEmail, getUserEmailLookupField } from '../../utils/open-drop-off-user-lookup';
import { useScreen } from '../useScreen';
import { MAX_FABLAB_SCAN_PAYLOAD_LENGTH, parseFablabScanQrCodeTransactionInfo } from './fablab-scan-qr-code.utils';

interface ViewProps {
    resolving: boolean;
    error: string | null;
    resetError: () => void;
    onBack: () => void;
    onNoQrCode: () => Promise<void>;
}

const SCREEN_ID = 'FABLAB_SCAN_QR_CODE';

const ACCEPTED_SENSOR_TYPES = new Set<string>([SensorType.QR_CODE, SensorType.TEXT]);

const createTransactionUserDraft = (userId: string, type: TransactionUserType, accountId: string, transactionId?: string): Partial<TransactionUser> => {
    const timestamp = new Date().toISOString();

    return {
        user_id: userId,
        type,
        transaction_id: transactionId || '',
        account_id: accountId,
        creation_timestamp: timestamp,
        update_timestamp: timestamp
    };
};

const findNextStep = (store: MobileWizardStateRecord, trigger: ScreenTrigger) =>
    store.steps.find((step) => step.screen === ScreenType.FABLAB_SCAN_QR_CODE && step.trigger === trigger);

const useFablabScanQrCode = (): ViewProps => {
    const intl = useIntl();
    const { navigationState, store, wizardId, updateWizardData, onBack, onSubmit } = useScreen();
    const { store: accountStore } = useAccountRepository();
    const { get: getSetting } = useWizardSettings(store);
    const { localUpdateTransaction } = useWizardTransaction({
        wizardId,
        store,
        autoCreate: false,
        autoFetch: false
    });
    const [resolving, setResolving] = useState(false);
    const [scanError, setScanError] = useState<string | null>(null);
    const transactionInfoInFlightRef = useRef(false);

    const resolveSenderId = (): string | null => navigationState.user?.id ?? getSetting(SettingKey.DEFAULT_SENDER_ID) ?? null;

    useScanners({
        id: SCREEN_ID,
        onDeviceSensorReceived: (payload, data) => {
            if (!ACCEPTED_SENSOR_TYPES.has(data?.type) || transactionInfoInFlightRef.current) return;

            if (payload.length > MAX_FABLAB_SCAN_PAYLOAD_LENGTH) {
                setScanError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.fablab-scan-qr-code.invalid-code',
                        description: 'Error shown when a scanned FabLab QR/text payload is invalid',
                        defaultMessage: 'This QR code is not valid. Please try again.'
                    })
                );
                return;
            }

            const handleTransactionInfo = async () => {
                if (!store) return;

                transactionInfoInFlightRef.current = true;
                setResolving(true);
                setScanError(null);

                try {
                    const transactionInfo = parseFablabScanQrCodeTransactionInfo(payload);
                    if (!transactionInfo) {
                        setScanError(
                            intl.formatMessage({
                                id: 'kiosk.wizard.fablab-scan-qr-code.invalid-code',
                                description: 'Error shown when a scanned FabLab QR/text payload is invalid',
                                defaultMessage: 'This QR code is not valid. Please try again.'
                            })
                        );
                        return;
                    }

                    const senderId = resolveSenderId();
                    if (!senderId) {
                        setScanError(
                            intl.formatMessage({
                                id: 'kiosk.wizard.fablab-scan-qr-code.sender-missing',
                                description: 'Error shown when FabLab scan has no logged-in user and no default sender setting',
                                defaultMessage: 'No sender is available for this drop-off. Please contact support.'
                            })
                        );
                        return;
                    }

                    const accountId = store.wizard.account_id;
                    const lookupField = getUserEmailLookupField(accountStore.availableAccountsInfo, accountId);
                    const candidateLists = await Promise.all(transactionInfo.receiverEmails.map((email) => findUsersByEmail(accountId, email, lookupField)));

                    const resolvedReceivers: IUser[] = [];
                    for (let index = 0; index < candidateLists.length; index++) {
                        const candidates = candidateLists[index];
                        if (candidates.length !== 1) {
                            Logger.warn('Skipping FabLab QR receiver that could not be uniquely resolved', {
                                receiverEmail: transactionInfo.receiverEmails[index],
                                lookupField,
                                candidateCount: candidates.length
                            });
                            continue;
                        }
                        resolvedReceivers.push(candidates[0]);
                    }

                    if (resolvedReceivers.length === 0) {
                        setScanError(
                            intl.formatMessage({
                                id: 'kiosk.wizard.fablab-scan-qr-code.no-receivers-found',
                                description: 'Error shown when none of the FabLab QR/text receiver emails match a user in the account',
                                defaultMessage: 'No receivers were found for this QR code. Please try again or continue without a QR code.'
                            })
                        );
                        return;
                    }

                    const receivers = resolvedReceivers.map((user) =>
                        createTransactionUserDraft(user.id, TransactionUserType.RECEIVER, accountId, store.data.transaction?.id)
                    );

                    await localUpdateTransaction({
                        tracking_number: transactionInfo.trackingNumber,
                        sender_ids: [senderId],
                        receiver_ids: resolvedReceivers.map((user) => user.id),
                        iot_id: navigationState.iot?.id ?? null,
                        user_id: navigationState.user?.id ?? null,
                        feature: Feature.FABLAB
                    });

                    updateWizardData({
                        trackingNumber: transactionInfo.trackingNumber,
                        senders: [createTransactionUserDraft(senderId, TransactionUserType.SENDER, accountId, store.data.transaction?.id)],
                        receivers
                    });

                    Logger.log('FabLab QR code processed', {
                        trackingNumber: transactionInfo.trackingNumber,
                        senderId,
                        receiverIds: resolvedReceivers.map((user) => user.id)
                    });

                    const nextStep = findNextStep(store, ScreenTrigger.SUBMIT);
                    onSubmit(nextStep);
                } catch (error) {
                    Logger.error('Failed to handle FabLab QR code', error);
                    setScanError(
                        intl.formatMessage({
                            id: 'kiosk.wizard.fablab-scan-qr-code.processing-failed',
                            description: 'Error shown when FabLab QR/text processing fails unexpectedly',
                            defaultMessage: 'Something went wrong while reading this QR code. Please try again.'
                        })
                    );
                } finally {
                    transactionInfoInFlightRef.current = false;
                    setResolving(false);
                }
            };

            void handleTransactionInfo();
        }
    });

    const onNoQrCode = async () => {
        if (!store) return;

        const nextStep = findNextStep(store, ScreenTrigger.SUBMIT_FALSE);
        if (!nextStep) return;

        const senderId = resolveSenderId();
        if (senderId) {
            await localUpdateTransaction({
                sender_ids: [senderId],
                user_id: navigationState.user?.id ?? null,
                iot_id: navigationState.iot?.id ?? null,
                feature: Feature.FABLAB
            });

            updateWizardData({
                senders: [createTransactionUserDraft(senderId, TransactionUserType.SENDER, store.wizard.account_id, store.data.transaction?.id)]
            });
        } else {
            Logger.warn('Cannot set FabLab no-QR sender: no logged-in user or default sender found');
        }

        setResolving(false);
        onSubmit(nextStep);
    };

    return {
        resolving,
        error: scanError,
        resetError: () => setScanError(null),
        onBack,
        onNoQrCode
    };
};

export default useFablabScanQrCode;
